import { WritingSettings } from "./writing/writing-field";
import { useEffect, useState } from "react";
import { getOrCreateAuthSession } from "./api/auth-client";
import { createWatchSessionGrant, openWatchCompanion } from "./api/watch-session-client";
import { getActiveUltraOrganizationId } from "./api/ultra-client";
import { fetchAppConfig, type AppRemoteConfig } from "./api/app-config-client";
import { clientEnv } from "./env";
import { moduleRegistry } from "./modules/module.registry";
import { canOpenCreatorWorkspace } from "./modules/creator-workspace/creator-access";
import { CreatorWorkspace } from "./modules/creator-workspace/creator-workspace";

const officialWebsiteHosts = new Set(["omnisynia.com", "www.omnisynia.com"]);

export function App() {
  const [activeModuleId, setActiveModuleId] = useState(initialModuleId);
  const [creatorOpen, setCreatorOpen] = useState(false);
  const [creatorUserId, setCreatorUserId] = useState<string | null>(null);
  const [creatorPending, setCreatorPending] = useState(false);
  const [creatorNotice, setCreatorNotice] = useState("");
  const [remoteConfig, setRemoteConfig] = useState<AppRemoteConfig>();
  const [watchConnecting, setWatchConnecting] = useState(false);
  const [watchMenuError, setWatchMenuError] = useState<string | null>(null);
  const visibleModules = moduleRegistry.filter((module) =>
    module.id !== "omnisynia-ultra" || remoteConfig?.features.omnisyniaUltra?.enabled !== false
  );
  const activeModule = visibleModules.find((module) => module.id === activeModuleId) ?? visibleModules[0];
  const ActiveModule = activeModule?.Component;
  const isOfficialWebsite = officialWebsiteHosts.has(window.location.hostname.toLowerCase());

  async function openCreator() {
    if (creatorPending) return;
    setCreatorPending(true);
    setCreatorNotice("");
    try {
      const session = await getOrCreateAuthSession();
      if (!canOpenCreatorWorkspace(session.user.role, session.user.mode)) {
        setCreatorNotice("Inicia sesión con una cuenta autorizada de Creador para entrar.");
        return;
      }
      setCreatorUserId(session.user.id);
      setCreatorOpen(true);
    } catch {
      setCreatorNotice("No se pudo verificar la sesión del creador.");
    } finally {setCreatorPending(false);}
  }

  async function connectAppleWatch() {
    if (watchConnecting) return;
    setWatchConnecting(true);
    setWatchMenuError(null);
    try {
      const session = await getOrCreateAuthSession();
      const grant = await createWatchSessionGrant(session.sessionToken, getActiveUltraOrganizationId() ?? undefined);
      openWatchCompanion(grant.code);
    } catch (error) {
      setWatchMenuError(error instanceof Error ? error.message : "No se pudo conectar el Apple Watch.");
    } finally {
      setWatchConnecting(false);
    }
  }
  useEffect(() => {
    const controller = new AbortController();
    fetchAppConfig(controller.signal).then(setRemoteConfig).catch(() => undefined);
    return () => controller.abort();
  }, []);

  if (remoteConfig?.maintenance.enabled) {
    return (
      <main className="system-notice maintenance-notice">
        <span className="brand-mark" aria-hidden="true">O</span>
        <p className="eyebrow">OMNISYNIA</p>
        <h1>Estamos mejorando la aplicación</h1>
        <p>Volveremos a estar disponibles en breve.</p>
      </main>
    );
  }

  return (
    <div className={creatorOpen ? "app-frame app-frame-creator" : "app-frame"}>
      <aside className="sidebar">
        <div className="brand-lockup">
          {isOfficialWebsite ? (
            <img
              className="brand-logo-official"
              src="/omnisynia-logo-official-web-v2.png"
              alt="Logo oficial de OMNISYNIA"
              decoding="async"
            />
          ) : (
            <span className="brand-mark" aria-hidden="true">
              O
            </span>
          )}
          <div>
            <strong>OMNISYNIA</strong>
            <small>ONE INTELLIGENCE · v1.1</small>
          </div>
        </div>

        <nav aria-label="Módulos OMNISYNIA">
          <p className="nav-title">MÓDULOS</p>
          {visibleModules.map((module) => (
            <button
              className={module.id === activeModuleId ? "module-link active" : "module-link"}
              key={module.id}
              onClick={() => setActiveModuleId(module.id)}
              type="button"
            >
              <span>◈</span>
              <span>
                <strong>{module.displayName}</strong>
                <small>{module.tier === "premium" ? "Premium" : "Core"}</small>
              </span>
            </button>
          ))}
        </nav>

        <div className="sidebar-footer">
          <span className="connection-dot" />
          Backend: {clientEnv.apiBaseUrl}
        </div>
      </aside>

      <main className="workspace">
        {remoteConfig?.version.updateAvailable && (
          <aside className={remoteConfig.version.updateRequired ? "update-banner required" : "update-banner"}>
            <div>
              <strong>{remoteConfig.version.updateRequired ? "Actualización necesaria" : "Actualización disponible"}</strong>
              <span>{remoteConfig.version.message}</span>
            </div>
            {remoteConfig.version.storeUrl && (
              <a href={remoteConfig.version.storeUrl}>Actualizar ahora</a>
            )}
          </aside>
        )}
        <header className="topbar">
          <div>
            <p className="eyebrow">OMNISYNIA — One Intelligence</p>
            <h1>{creatorOpen ? "ONE CORE · Creador" : activeModule?.displayName ?? "Chat"}</h1>
          </div>
          <div className="topbar-actions">
            <button className="creator-entry" type="button" disabled={creatorPending} onClick={() => void openCreator()}>{creatorPending ? "Comprobando..." : "Creador"}</button>
            {isOfficialWebsite && (
              <aside className="official-web-download" aria-label="Descarga OMNISYNIA en App Store y Google Play">
                <span className="official-web-download-viewport">
                  <img
                    src="/omnisynia-download-web.png?v=2"
                    alt="OMNISYNIA — Descarga la app en App Store y Google Play"
                    decoding="async"
                  />
                  <a
                    className="official-store-link official-store-link--apple"
                    href="https://apps.apple.com/es/search?term=OMNISYNIA"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Buscar OMNISYNIA en App Store"
                    title="Abrir App Store"
                  />
                  <a
                    className="official-store-link official-store-link--google"
                    href="https://play.google.com/store/search?q=OMNISYNIA&c=apps"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Buscar OMNISYNIA en Google Play"
                    title="Abrir Google Play"
                  />
                </span>
              </aside>
            )}
            <span className="version-badge">BUILD {clientEnv.appVersion}</span>
            <details className="app-overflow-menu">
              <summary aria-label="Más opciones" title="Más opciones">⋯</summary>
              <div className="app-overflow-popover">
                <WritingSettings />
                <button className="app-overflow-item" type="button" onClick={event => {
                  event.currentTarget.closest("details")?.removeAttribute("open");
                  setCreatorOpen(false);
                  setActiveModuleId("omni-wiki");
                }}><span aria-hidden="true">▤</span><span>OMNI WIKI</span></button>
                <button
                  className="app-overflow-item"
                  disabled={watchConnecting}
                  onClick={() => void connectAppleWatch()}
                  type="button"
                >
                  <svg className="app-watch-icon" viewBox="0 0 24 24" aria-hidden="true">
                    <rect x="7" y="6" width="10" height="12" rx="2.5" />
                    <path d="M9 6V3.5A1.5 1.5 0 0 1 10.5 2h3A1.5 1.5 0 0 1 15 3.5V6M9 18v2.5a1.5 1.5 0 0 0 1.5 1.5h3a1.5 1.5 0 0 0 1.5-1.5V18M10 9h4v6h-4z" />
                  </svg>
                  <span>{watchConnecting ? "Conectando…" : "Conectar Apple Watch"}</span>
                </button>
                <button
                  className="app-overflow-item"
                  onClick={event => {
                    event.currentTarget.closest("details")?.removeAttribute("open");
                    setCreatorOpen(false);
                    setActiveModuleId("chat-omnisynia");
                  }}
                  type="button"
                >
                  <span aria-hidden="true">💬</span>
                  <span>Ir al chat</span>
                </button>
                {watchMenuError ? <p className="app-overflow-error" role="alert">{watchMenuError}</p> : null}
              </div>
            </details>
          </div>
        </header>

        {creatorNotice && <p role="status" className="creator-auth-notice">{creatorNotice}</p>}
        {creatorOpen && creatorUserId ? (
          <CreatorWorkspace key={creatorUserId} userId={creatorUserId}
            onExit={() => {setCreatorOpen(false);setActiveModuleId("chat-omnisynia");}}
            onOpenAccount={() => {setCreatorOpen(false);setActiveModuleId("account");}} />
        ) : ActiveModule ? (
          <ActiveModule />
        ) : (
          <section className="empty-state">
            <h2>Módulo no disponible</h2>
            <p>Selecciona un módulo registrado para continuar.</p>
          </section>
        )}
      </main>
    </div>
  );
}

function initialModuleId(): string {
  const params = new URLSearchParams(window.location.search);
  if (params.has("accountAction") || params.has("billing")) return "account";
  // Bookmarks and native links to retired editors now open the existing chat.
  if (params.has("crea")) return "chat-omnisynia";
  const requested = params.get("module");
  if (requested && moduleRegistry.some(module => module.id === requested)) return requested;
  return moduleRegistry[0]?.id ?? "";
}

